import { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useLocalization } from "../../contexts/LocalizationContext";
import styles from "./StrategyFullReportModal.module.css";
import {
  SitePerformanceViewer,
  SitePerformanceAssessmentViewerV2,
  IndexingViewer,
  IndexingAssessmentViewerV2,
  GscReportViewer,
  GscReportAssessmentViewerV2,
  CoreWebVitalsViewer,
  MerchantCenterViewer,
  MerchantCenterAssessmentViewerV2,
} from "../assessmentViewer/FoundationStage";
import {
  ShopifySync,
  GoogleStack,
  MetaStack,
  PriorityBlocker,
  OnboardingConclusion,
} from "../assessmentViewer/OnboardingStage";
import {
  ExecutivePulse,
  ShopifySyncSnapshot,
  GoogleActivationBoard,
  MetaActivationBoard,
  ActivationChecklist,
  ReadinessTargets,
  WhyItMattersOnboarding,
} from "../strategyViewer/OnboardingStage";
import {
  ExecutiveSpark,
  CrawlAndIndexing,
  ProductIntelligenceDeepClean,
  ShopSeoRemediation,
  BlogSeoUplift,
  StorewideCompliance,
  RoadmapMilestones,
} from "../strategyViewer/FoundationalStage";
import FunnelStageKfaCard from "../strategyViewer/FunnelStage/FunnelStageKfaCard";
import {
  FunnelAssessmentCard,
  FunnelRevenueReality,
  FunnelChannelRealOrders,
  FunnelReturnsRefunds,
  FunnelUnitEconomics,
  FunnelCacPayback,
  FunnelRepeatCustomers,
  FunnelScaleSpend,
  FunnelRevenueDiversification,
  FunnelDiscountDependency,
  FunnelRepeatabilityScore,
  FunnelFixedCostViability,
  FunnelProductSkuEconomics,
  FunnelCustomerLifetimeEconomics,
  FunnelGrowthEfficiency,
  FunnelMarginStability,
  FunnelOperationalReadiness,
  FunnelRevenueRealityAssessmentViewerV2,
  FunnelFixedCostViabilityAssessmentViewerV2,
  FunnelProductSkuEconomicsAssessmentViewerV2,
  FunnelCustomerLifetimeEconomicsAssessmentViewerV2,
  FunnelGrowthEfficiencyAssessmentViewerV2,
  FunnelMarginStabilityAssessmentViewerV2,
  FunnelOperationalReadinessAssessmentViewerV2,
  GenericFunnelViewer,
} from "../assessmentViewer/FunnelStage";
import {
  BarChart3,
  Building2,
  Calculator,
  ClipboardCheck,
  FileText,
  HeartPulse,
  Layers,
  Medal,
  Package,
  Percent,
  Repeat,
  RotateCcw,
  Route,
  Scale,
  ShieldCheck,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

const extractTitleFromMarkdown = (markdown = "") => {
  if (typeof markdown !== "string") return "Section";
  const boldMatch = markdown.match(/\*\*(.+?)\*\*/);
  if (boldMatch && boldMatch[1]) return boldMatch[1].trim();
  const firstLine = markdown.split(/\r?\n/)[0] || "";
  const cleaned = firstLine.replace(/^[#*>]+\s*/, "").trim();
  return cleaned || "Section";
};

const stripTitleFromMarkdown = (markdown = "") => {
  if (typeof markdown !== "string") return "";
  return markdown.replace(/^\s*\*\*[^*]+\*\*\s*/, "").trim();
};

const normalizePayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.map(String);
  if (typeof payload === "string") return [payload];
  return [JSON.stringify(payload, null, 2)];
};

const ONBOARDING_TITLES = new Set([
  "shopify sync",
  "connect shopify",
  "google stack",
  "connect google",
  "meta stack",
  "connect meta",
  "priority blockers",
  "conclusion",
  "executive pulse",
  "zeqi onboarding overview",
  "shopify sync snapshot",
  "google activation board",
  "meta activation board",
  "activation checklist",
  "readiness targets",
  "why it matters",
]);

const stripOnboardingHeading = (md = "") => {
  const normalized = String(md || "").replace(/\r/g, "");
  const match = normalized.match(/^\s*\*\*[^*]+\*\*\s*/);
  return match ? normalized.slice(match[0].length) : normalized;
};

const getNumberedFunnelTitle = (title = "") => {
  const normalized = String(title || "")
    .trim()
    .toLowerCase()
    .replace(/^\d+(\.\d+)?\.\s*/, "")
    .replace(/[—–]/g, "-")
    .replace(/\s+/g, " ");
  const map = {
    "revenue reality": "1. Revenue Reality",
    "returns & refunds - revenue reality":
      "1.1 Returns & Refunds — Revenue Reality",
    "unit economics - do we make money per order?":
      "1.2 Unit Economics - Do We Make Money Per Order?",
    "does cac make sense vs what the customer pays?":
      "1.3 Does CAC make sense vs What the customer pays?",
    "which channel brings real orders, not clicks?":
      "1.4 Which Channel Brings Real Orders, Not Clicks?",
    "do customers come back?": "1.5 Do customers come back?",
    "if we scale spend, do we still win?":
      "1.6 If we scale spend, Do we still win?",
    "is revenue diversified enough to be repeatable?":
      "1.7 Is Revenue Diversified Enough to be Repeatable?",
    "is margin dependent on discounts?":
      "1.8 Is Margin Dependent on Discounts?",
    "revenue repeatability score (rrs)":
      "1.9 Revenue Repeatability Score (RRS)",
    "fixed cost viability diagnostic": "2. Fixed Cost Viability Diagnostic",
    "product & sku economics": "3. Product & SKU Economics",
    "customer lifetime economics": "4. Customer Lifetime Economics",
    "growth efficiency & channel mix": "5. Growth Efficiency & Channel Mix",
    "margin stability & risk": "6. Margin Stability & Risk",
    "operational readiness & data confidence":
      "7. Operational Readiness & Data Confidence",
  };
  return map[normalized] || title;
};

const onboardingLines = (md = "") =>
  stripOnboardingHeading(md)
    .split(/\r?\n/)
    .map((line) => line.trim());

const parseOnboardingStats = (md = "") => {
  const lines = onboardingLines(md);
  const stats = [];
  const summaryLines = [];
  let summaryMode = false;

  for (const line of lines) {
    if (!line) {
      if (!summaryMode && stats.length) summaryMode = true;
      continue;
    }

    if (!summaryMode && line.includes(":")) {
      const [key, ...rest] = line.split(":");
      let value = rest.join(":").trim();
      value = value.replace(/[.,]$/, "");
      stats.push({ key: key.trim(), value });
      continue;
    }

    summaryMode = true;
    summaryLines.push(line.replace(/^[-*\u2022]\s*/, ""));
  }

  return { stats, summary: summaryLines.join(" ").trim() };
};

const parseOnboardingBullets = (md = "") =>
  onboardingLines(md)
    .filter((line) => /^[-*\u2022]/.test(line))
    .map((line) => line.replace(/^[-*\u2022]\s*/, ""));

const parseOnboardingText = (md = "") =>
  onboardingLines(md).filter(Boolean).join(" ").trim();

// const getSectionMeta = (title = "") => {
//   const t = title.toLowerCase();

//   if (t.includes("indexing")) {
//     return {
//       icon: FileText,
//       label: "Index coverage and crawl",
//       bg: "#EFF6FF",
//       accent: "#1D4ED8",
//     };
//   }
//   if (t.includes("site performance")) {
//     return {
//       icon: Zap,
//       label: "Page speed and UX",
//       bg: "#ECFDF3",
//       accent: "#16A34A",
//     };
//   }
//   if (t.includes("core web vitals")) {
//     return {
//       icon: HeartPulse,
//       label: "Core Web Vitals health",
//       bg: "#FEF9C3",
//       accent: "#CA8A04",
//     };
//   }
//   if (t === "report") {
//     return {
//       icon: TrendingUp,
//       label: "Clicks and impressions",
//       bg: "#F5F3FF",
//       accent: "#7C3AED",
//     };
//   }
//   if (t.includes("ctr")) {
//     return {
//       icon: TrendingDown,
//       label: "Low click-through queries",
//       bg: "#FFF7ED",
//       accent: "#EA580C",
//     };
//   }
//   if (t.includes("merchant center")) {
//     return {
//       icon: ShieldCheck,
//       label: "Trust and policy signals",
//       bg: "#ECFEFF",
//       accent: "#0E7490",
//     };
//   }
//   if (t.includes("revenue reality")) {
//     return {
//       icon: BarChart3,
//       label: "Revenue health",
//       bg: "#F1F5F9",
//       accent: "#0F172A",
//     };
//   }
//   if (t.includes("returns") || t.includes("refunds")) {
//     return {
//       icon: RotateCcw,
//       label: "Leakage signals",
//       bg: "#FFF7ED",
//       accent: "#EA580C",
//     };
//   }
//   if (t.includes("unit economics") || t.includes("make money per order")) {
//     return {
//       icon: Calculator,
//       label: "Unit economics",
//       bg: "#EEF2FF",
//       accent: "#4F46E5",
//     };
//   }
//   if (t.includes("cac") || t.includes("ltv")) {
//     return {
//       icon: Scale,
//       label: "Acquisition efficiency",
//       bg: "#ECFDF3",
//       accent: "#16A34A",
//     };
//   }
//   if (t.includes("customers come back") || t.includes("customer lifetime")) {
//     return {
//       icon: Users,
//       label: "Retention and LTV",
//       bg: "#F0F9FF",
//       accent: "#0284C7",
//     };
//   }
//   if (t.includes("scale spend")) {
//     return {
//       icon: TrendingUp,
//       label: "Scale viability",
//       bg: "#F0FDF4",
//       accent: "#16A34A",
//     };
//   }
//   if (t.includes("revenue diversified") || t.includes("diversified")) {
//     return {
//       icon: Layers,
//       label: "Revenue mix",
//       bg: "#F8FAFC",
//       accent: "#475569",
//     };
//   }
//   if (t.includes("margin dependent") || t.includes("discount")) {
//     return {
//       icon: Percent,
//       label: "Discount dependency",
//       bg: "#FDF2F8",
//       accent: "#DB2777",
//     };
//   }
//   if (t.includes("repeatability score") || t.includes("rrs")) {
//     return {
//       icon: Medal,
//       label: "Repeatability",
//       bg: "#FFFBEB",
//       accent: "#B45309",
//     };
//   }
//   if (t.includes("fixed cost viability")) {
//     return {
//       icon: Building2,
//       label: "Fixed cost viability",
//       bg: "#F8FAFC",
//       accent: "#0F172A",
//     };
//   }
//   if (t.includes("product") && t.includes("sku")) {
//     return {
//       icon: Package,
//       label: "SKU economics",
//       bg: "#F8FAFC",
//       accent: "#0F172A",
//     };
//   }
//   if (t.includes("growth") && t.includes("efficiency")) {
//     return {
//       icon: Route,
//       label: "Channel efficiency",
//       bg: "#F0FDF4",
//       accent: "#16A34A",
//     };
//   }
//   if (t.includes("margin stability") || t.includes("margin risk")) {
//     return {
//       icon: ShieldAlert,
//       label: "Margin risk",
//       bg: "#FEF2F2",
//       accent: "#DC2626",
//     };
//   }
//   if (t.includes("operational readiness") || t.includes("data confidence")) {
//     return {
//       icon: ClipboardCheck,
//       label: "Operational readiness",
//       bg: "#EFF6FF",
//       accent: "#1D4ED8",
//     };
//   }

//   return {
//     icon: BarChart3,
//     label: "Key signal",
//     bg: "#E2E8F0",
//     accent: "#0F172A",
//   };
// };

const getKeyMetric = (title = "", markdown = "") => {
  const t = title.toLowerCase();
  if (t.includes("indexing")) {
    const m = markdown.match(/Coverage Ratio:\s*([\d.]+)%/i);
    if (m) {
      const v = Number(m[1]);
      if (!Number.isNaN(v))
        return { label: "Index coverage", value: v, unit: "%", max: 100 };
    }
  }
  if (t.includes("site performance")) {
    const m = markdown.match(/Performance:\s*([\d.]+)/i);
    if (m) {
      const v = Number(m[1]);
      if (!Number.isNaN(v))
        return { label: "Performance score", value: v, unit: "", max: 100 };
    }
  }
  if (t.includes("core web vitals")) {
    const m = markdown.match(/takes about\s*([\d.]+)\s*seconds/i);
    if (m) {
      const v = Number(m[1]);
      if (!Number.isNaN(v))
        return { label: "Mobile LCP", value: v, unit: "s", max: 6 };
    }
  }
  if (t === "report") {
    const m = markdown.match(/Average CTR:\s*([\d.]+)%/i);
    if (m) {
      const v = Number(m[1]);
      if (!Number.isNaN(v))
        return { label: "Average CTR", value: v, unit: "%", max: 10 };
    }
  }
  return null;
};

const findFirstTableBlock = (markdown = "") => {
  const lines = markdown.split(/\r?\n/);
  const start = lines.findIndex((line) => /^\s*\|.*\|\s*$/.test(line));
  if (start === -1) return null;
  if (!/^\s*\|?[:\-\s|]+\|?\s*$/.test(lines[start + 1] || "")) return null;

  let i = start + 2;
  while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) i++;

  const header = lines[start];
  const rows = lines.slice(start + 2, i);
  const headers = header
    .replace(/^\s*\||\|\s*$/g, "")
    .split("|")
    .map((cell) => cell.trim());
  const parsedRows = rows.map((row) =>
    row
      .replace(/^\s*\||\|\s*$/g, "")
      .split("|")
      .map((cell) => cell.trim()),
  );

  return {
    headers,
    rows: parsedRows,
    before: lines.slice(0, start).join("\n"),
    after: lines.slice(i).join("\n"),
  };
};

const renderParagraphs = (content) => {
  const lines = String(content || "").split(/\r?\n/);
  const blocks = [];
  let listItems = [];

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul className={styles.list} key={`list-${blocks.length}`}>
        {listItems.map((item, idx) => (
          <li key={`${item}-${idx}`}>{item}</li>
        ))}
      </ul>,
    );
    listItems = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (/^[-*\u2022]/.test(line)) {
      listItems.push(line.replace(/^[-*\u2022]\s*/, ""));
      return;
    }
    flushList();
    blocks.push(
      <p className={styles.paragraph} key={`p-${idx}`}>
        {line}
      </p>,
    );
  });

  flushList();
  return blocks;
};

const renderTable = (table) => {
  if (!table) return null;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {table.headers.map((header) => (
              <th key={header} className={styles.tableHead}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIdx) => (
            <tr
              key={`row-${rowIdx}`}
              className={rowIdx % 2 === 0 ? styles.tableRowAlt : undefined}
            >
              {row.map((cell, cellIdx) => (
                <td
                  key={`cell-${rowIdx}-${cellIdx}`}
                  className={styles.tableCell}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StrategyFullReportModal = ({
  isOpen,
  clusterFlag,
  content,
  onClose,
  variant = "modal",
  initialSectionIndex = 0,
  showCloseButton = true,
}) => {
  const { locale } = useLocalization();
  const isRTL = locale === "ar";
  const scrollRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(initialSectionIndex);
  const [isInitialPositionReady, setIsInitialPositionReady] = useState(false);

  const sections = normalizePayload(content).map((entry) => {
    const sectionTitle = extractTitleFromMarkdown(entry);
    const sectionBody = stripTitleFromMarkdown(entry);
    return { title: sectionTitle, rawTitle: sectionTitle, body: sectionBody };
  });

  const fromOnboardingFlag =
    typeof clusterFlag === "string" &&
    clusterFlag.trim().toLowerCase().includes("onboarding");

  const looksLikeOnboardingStage = sections.some((section) =>
    ONBOARDING_TITLES.has(section.title.trim().toLowerCase()),
  );

  const isOnboardingStageStrategy = !!(
    fromOnboardingFlag || looksLikeOnboardingStage
  );

  const fromFunnelFlag =
    typeof clusterFlag === "string" &&
    clusterFlag.trim().toLowerCase().includes("funnel");

  const looksLikeFunnelStage = sections.some((section) => {
    const t = section.title.trim().toLowerCase();
    return (
      t === "indexing exercise (technical + copy writing)" ||
      t === "email generation" ||
      t === "seo (blogs + keyword optimization)" ||
      t === "pdp optimization" ||
      t === "homepage optimization" ||
      t === "pricing exercise" ||
      t === "instagram / facebook post creation" ||
      t === "paid ad (campaign + ad set)" ||
      t === "product intelligence, forecast and ordering" ||
      t === "order economics" ||
      t === "customer economics" ||
      t === "leakage control"
    );
  });

  const isFunnelStageStrategy = !!(fromFunnelFlag || looksLikeFunnelStage);

  const FOUNDATIONAL_TITLES = new Set([
    "executive spark",
    "crawl & indexing",
    "product intelligence deep clean",
    "shop seo remediation",
    "blog seo uplift",
    "storewide & policy compliance",
    "roadmap & milestones",
  ]);

  const fromFoundationalFlag =
    typeof clusterFlag === "string" &&
    clusterFlag.trim().toLowerCase().includes("foundation");

  const looksLikeFoundationalStage = sections.some((section) =>
    FOUNDATIONAL_TITLES.has(section.title.trim().toLowerCase()),
  );

  const isFoundationalStageStrategy = !!(
    fromFoundationalFlag || looksLikeFoundationalStage
  );

  // Position carousel on the selected section before paint to avoid visible jump.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const validIndex = Math.min(
      Math.max(0, initialSectionIndex),
      sections.length - 1,
    );

    const applyIndexToCarousel = () => {
      if (!scrollRef.current) return;
      const slide = scrollRef.current.querySelector("[data-carousel-slide]");
      if (!slide) return;
      const width = slide.offsetWidth || scrollRef.current.clientWidth || 1;
      const maxScroll = Math.max(
        0,
        scrollRef.current.scrollWidth - scrollRef.current.clientWidth,
      );
      let left = width * validIndex;
      if (isRTL) {
        left = Math.max(0, maxScroll - left);
        if (scrollRef.current.scrollLeft < 0) left = -left;
      }
      scrollRef.current.scrollLeft = left;
    };

    applyIndexToCarousel();
    const rafId = window.requestAnimationFrame(() => {
      applyIndexToCarousel();
      setCurrentIndex(validIndex);
      setIsInitialPositionReady(true);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isOpen, initialSectionIndex, isRTL, sections.length]);

  const getNormalizedScrollLeft = useCallback(
    (node) => {
      const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
      let left = node.scrollLeft;

      if (isRTL) {
        if (left < 0) {
          left = Math.abs(left);
        } else {
          left = maxScroll - left;
        }
      }

      return { left, maxScroll };
    },
    [isRTL],
  );

  const handleScroll = () => {
    if (scrollTimerRef.current) window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(() => {
      const node = scrollRef.current;
      if (!node) return;
      const slide = node.querySelector("[data-carousel-slide]");
      const width = slide?.offsetWidth || node.clientWidth || 1;
      const { left } = getNormalizedScrollLeft(node);
      const raw = left / width;
      const idx = Math.min(sections.length - 1, Math.max(0, Math.round(raw)));
      setCurrentIndex(idx);
    }, 80);
  };

  const scrollToIndex = useCallback(
    (idx) => {
      const node = scrollRef.current;
      if (!node) return;
      const slide = node.querySelector("[data-carousel-slide]");
      const width = slide?.offsetWidth || node.clientWidth || 1;
      const { maxScroll } = getNormalizedScrollLeft(node);
      let left = width * idx;
      if (isRTL) {
        left = Math.max(0, maxScroll - left);
        if (node.scrollLeft < 0) left = -left;
      }
      node.scrollTo({ left, behavior: "smooth" });
    },
    [getNormalizedScrollLeft, isRTL],
  );

  const nextIndex = useCallback(
    () => Math.min(sections.length - 1, currentIndex + 1),
    [currentIndex, sections.length],
  );

  const prevIndex = useCallback(
    () => Math.max(0, currentIndex - 1),
    [currentIndex],
  );

  useEffect(() => {
    if (!isOpen || sections.length <= 1) return;
    const onKey = (event) => {
      if (event.key === "ArrowRight") {
        const target = isRTL ? prevIndex() : nextIndex();
        scrollToIndex(target);
      }
      if (event.key === "ArrowLeft") {
        const target = isRTL ? nextIndex() : prevIndex();
        scrollToIndex(target);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isRTL, nextIndex, prevIndex, scrollToIndex, sections.length]);

  const renderSection = (section, idx) => (
    <div key={`${section.title}-${idx}`} className={styles.sectionCard}>
      {(() => {
        // const meta = getSectionMeta(section.title);
        const metric = getKeyMetric(section.title, section.body);
        const tableBlock = findFirstTableBlock(section.body);
        const before = tableBlock?.before ?? section.body;
        const after = tableBlock?.after ?? "";
        const percent = metric
          ? Math.min(100, Math.max(0, (metric.value / metric.max) * 100))
          : 0;
        const normalizedTitle = (section.rawTitle || section.title)
          .trim()
          .toLowerCase();
        const assessmentKey = (() => {
          if (normalizedTitle.includes("site performance"))
            return "sitePerformance";
          if (normalizedTitle.includes("core web vitals"))
            return "coreWebVitals";
          if (normalizedTitle.includes("indexing")) return "indexing";
          if (normalizedTitle.includes("merchant center"))
            return "merchantCenter";
          if (normalizedTitle === "report") return "gscReport";
          return null;
        })();

        const isAssessmentV2 = (() => {
          const body = String(section.body || "");
          if (assessmentKey === "indexing") {
            return /\*\*\s*Indexing Assessment\s*\*\*/i.test(body);
          }
          if (assessmentKey === "sitePerformance") {
            return /\*\*\s*Core Web Vitals Assessment\s*\*\*/i.test(body);
          }
          if (assessmentKey === "gscReport") {
            return /\*\*\s*Performance Report Assessment\s*\*\*/i.test(body);
          }
          if (assessmentKey === "merchantCenter") {
            return /\*\*\s*Merchant Center Assessment\s*\*\*/i.test(body);
          }
          return false;
        })();

        const renderAssessment = () => {
          if (assessmentKey === "sitePerformance") {
            if (isAssessmentV2) {
              return (
                <SitePerformanceAssessmentViewerV2 markdown={section.body} />
              );
            }
            return <SitePerformanceViewer markdown={section.body} />;
          }
          if (assessmentKey === "coreWebVitals") {
            return <CoreWebVitalsViewer markdown={section.body} />;
          }
          if (assessmentKey === "indexing") {
            if (isAssessmentV2) {
              return <IndexingAssessmentViewerV2 markdown={section.body} />;
            }
            return <IndexingViewer markdown={section.body} />;
          }
          if (assessmentKey === "merchantCenter") {
            if (isAssessmentV2) {
              return (
                <MerchantCenterAssessmentViewerV2 markdown={section.body} />
              );
            }
            return <MerchantCenterViewer markdown={section.body} />;
          }
          if (assessmentKey === "gscReport") {
            if (isAssessmentV2) {
              return <GscReportAssessmentViewerV2 markdown={section.body} />;
            }
            return <GscReportViewer markdown={section.body} />;
          }
          return null;
        };

        const funnelAssessmentMeta = {
          "revenue reality (top-line health)": {
            subtitle: "Top-line health from revenue and demand signals.",
            badge: "REVENUE",
          },
          "gross margin & cogs (product truth)": {
            subtitle: "Product profitability and cost structure snapshot.",
            badge: "MARGIN",
          },
          "contribution margin (cm1 -> cm2)": {
            subtitle: "Contribution economics after costs and shipping.",
            badge: "CM",
          },
          "cac vs ltv (scalability test)": {
            subtitle: "Acquisition efficiency versus customer value.",
            badge: "CAC/LTV",
          },
          "returns, refunds & leakage": {
            subtitle: "Leakage signals from refunds and cancellations.",
            badge: "LEAKAGE",
          },
          "customer acquisition mix (risk profile)": {
            subtitle: "Channel concentration and acquisition risk mix.",
            badge: "MIX",
          },
          "competitive pressure & market position": {
            subtitle: "Competitive density and differentiation pressure.",
            badge: "COMPETITION",
          },
          "operational readiness": {
            subtitle: "Execution readiness across crawl and tasks.",
            badge: "OPS",
          },
          "structural signals & constraints (emergent synthesis)": {
            subtitle: "Surface area and structural constraints snapshot.",
            badge: "STRUCTURE",
          },
          "core diagnosis (point a)": {
            subtitle: "Current operating zone and constraints.",
            badge: "DIAGNOSIS",
          },
          "what this means before strategy": {
            subtitle: "Boundary between diagnosis and strategy.",
            badge: "BOUNDARY",
          },
        };

        const renderStageContent = () => {
          if (isOnboardingStageStrategy) {
            if (normalizedTitle === "shopify sync") {
              const payload = parseOnboardingStats(section.body);
              return (
                <ShopifySync
                  title={section.title}
                  stats={payload.stats}
                  summary={payload.summary}
                />
              );
            }
            if (normalizedTitle === "google stack") {
              const payload = parseOnboardingStats(section.body);
              return (
                <GoogleStack
                  title={section.title}
                  statuses={payload.stats}
                  summary={payload.summary}
                />
              );
            }
            if (normalizedTitle === "meta stack") {
              const payload = parseOnboardingStats(section.body);
              return (
                <MetaStack
                  title={section.title}
                  statuses={payload.stats}
                  summary={payload.summary}
                />
              );
            }
            if (normalizedTitle === "priority blockers") {
              const items = parseOnboardingBullets(section.body);
              return <PriorityBlocker title={section.title} items={items} />;
            }
            if (normalizedTitle === "conclusion") {
              const text = parseOnboardingText(section.body);
              return <OnboardingConclusion title={section.title} text={text} />;
            }
            if (normalizedTitle === "zeqi onboarding overview") {
              return <ExecutivePulse markdown={section.body} />;
            }
            if (normalizedTitle === "connect shopify") {
              return <ShopifySyncSnapshot markdown={section.body} />;
            }
            if (normalizedTitle === "connect google") {
              return <GoogleActivationBoard markdown={section.body} />;
            }
            if (normalizedTitle === "connect meta") {
              return <MetaActivationBoard markdown={section.body} />;
            }
            if (normalizedTitle === "executive pulse") {
              return <ExecutivePulse markdown={section.body} />;
            }
            if (normalizedTitle === "shopify sync snapshot") {
              return <ShopifySyncSnapshot markdown={section.body} />;
            }
            if (normalizedTitle === "google activation board") {
              return <GoogleActivationBoard markdown={section.body} />;
            }
            if (normalizedTitle === "meta activation board") {
              return <MetaActivationBoard markdown={section.body} />;
            }
            if (normalizedTitle === "activation checklist") {
              return <ActivationChecklist markdown={section.body} />;
            }
            if (normalizedTitle === "readiness targets") {
              return <ReadinessTargets markdown={section.body} />;
            }
            if (normalizedTitle === "why it matters") {
              return <WhyItMattersOnboarding markdown={section.body} />;
            }
          }

          if (isFoundationalStageStrategy) {
            if (normalizedTitle === "executive spark") {
              return <ExecutiveSpark markdown={section.body} />;
            }
            if (normalizedTitle === "crawl & indexing") {
              return <CrawlAndIndexing markdown={section.body} />;
            }
            if (normalizedTitle === "product intelligence deep clean") {
              return <ProductIntelligenceDeepClean markdown={section.body} />;
            }
            if (normalizedTitle === "shop seo remediation") {
              return <ShopSeoRemediation markdown={section.body} />;
            }
            if (normalizedTitle === "blog seo uplift") {
              return <BlogSeoUplift markdown={section.body} />;
            }
            if (normalizedTitle === "storewide & policy compliance") {
              return <StorewideCompliance markdown={section.body} />;
            }
            if (normalizedTitle === "roadmap & milestones") {
              return <RoadmapMilestones markdown={section.body} />;
            }
          }

          if (isFunnelStageStrategy) {
            const t = normalizedTitle;
            const funnelTitle = getNumberedFunnelTitle(
              section.rawTitle || section.title,
            );
            const funnelMarkdown = `**${funnelTitle}**\n${section.body || ""}`;
            const isFunnelKfa =
              t === "indexing exercise (technical + copy writing)" ||
              t === "email generation" ||
              t === "seo (blogs + keyword optimization)" ||
              t === "pdp optimization" ||
              t === "homepage optimization" ||
              t === "pricing exercise" ||
              t === "instagram / facebook post creation" ||
              t === "paid ad (campaign + ad set)" ||
              t === "product intelligence, forecast and ordering" ||
              t === "order economics" ||
              t === "customer economics" ||
              t === "leakage control";

            if (isFunnelKfa) {
              return <FunnelStageKfaCard markdown={section.body} />;
            }

            const isRevenueReality =
              !t.includes("returns") && t.includes("revenue reality");
            const isChannelRealOrders =
              t.includes("channel") && t.includes("real orders");
            const isReturnsRefundsRevenueReality =
              t.includes("returns") && t.includes("reality");
            const isCacPayback =
              t.includes("cac make sense") ||
              t.includes("what the customer pays") ||
              t.includes("cac vs ltv");
            const isCustomerRetention = t.includes("customers come back");
            const isScaleSpend = t.includes("scale spend");
            const isExecutiveSnapshot = t === "executive snapshot";
            const isPointA = t === "point a";
            const isPointB = t === "point b";
            const isTopInitiatives = t.includes("top 3 initiatives");
            const isRoadmap = t === "roadmap";
            const isRisksAndAssumptions = t.includes("risks and assumptions");
            const isFixedCostViability = t.includes("fixed cost viability");
            const isRevenueDiversified = t.includes("revenue diversified");
            const isMarginDependent = t.includes("margin dependent");
            const isRepeatabilityScore =
              t.includes("repeatability score") || t.includes("rrs");
            const isMarketReality = t.includes("market reality");
            const isProductPerformanceReality = t.includes(
              "product performance reality",
            );
            const isProductTruth = t.includes("product truth");
            const isHomepageMessagingSignals =
              t.includes("homepage") && t.includes("messaging");
            const isRevenueConcentrationRisk = t.includes(
              "revenue concentration",
            );
            const isUnitEconomics =
              t.includes("unit economics") ||
              t.includes("make money per order");
            const isCompetitivePressureSnapshot = t.includes(
              "competitive pressure",
            );
            const isCoreDiagnosis = t.includes("core diagnosis");
            const isProductSkuEconomics =
              t.includes("product") &&
              t.includes("sku") &&
              t.includes("economics");
            const isCustomerLifetimeEconomics =
              t.includes("customer") &&
              t.includes("lifetime") &&
              t.includes("economics");
            const isGrowthEfficiencyChannelMix =
              (t.includes("growth") && t.includes("efficiency")) ||
              (t.includes("channel") && t.includes("mix"));
            const isMarginStabilityRisk =
              (t.includes("margin") && t.includes("stability")) ||
              (t.includes("margin") && t.includes("risk"));
            const isOperationalReadinessDataConfidence =
              (t.includes("operational") &&
                t.includes("readiness") &&
                t.includes("data")) ||
              (t.includes("data") && t.includes("confidence"));
            const hasFunnelAssessmentTitle = /\*\*[^*\n]+Assessment\*\*/i.test(
              section.body || "",
            );

            if (funnelAssessmentMeta[t]) {
              return (
                <FunnelAssessmentCard
                  markdown={funnelMarkdown}
                  meta={funnelAssessmentMeta[t]}
                />
              );
            }

            if (isReturnsRefundsRevenueReality) {
              return <FunnelReturnsRefunds markdown={funnelMarkdown} />;
            }
            if (isRevenueReality) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelRevenueRealityAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return <FunnelRevenueReality markdown={funnelMarkdown} />;
            }
            if (isChannelRealOrders) {
              return <FunnelChannelRealOrders markdown={funnelMarkdown} />;
            }
            if (isUnitEconomics) {
              return <FunnelUnitEconomics markdown={funnelMarkdown} />;
            }
            if (isCacPayback) {
              return <FunnelCacPayback markdown={funnelMarkdown} />;
            }
            if (isCustomerRetention) {
              return <FunnelRepeatCustomers markdown={funnelMarkdown} />;
            }
            if (isScaleSpend) {
              return <FunnelScaleSpend markdown={funnelMarkdown} />;
            }
            if (isRevenueDiversified) {
              return <FunnelRevenueDiversification markdown={funnelMarkdown} />;
            }
            if (isMarginDependent) {
              return <FunnelDiscountDependency markdown={funnelMarkdown} />;
            }
            if (isRepeatabilityScore) {
              return <FunnelRepeatabilityScore markdown={funnelMarkdown} />;
            }
            if (isFixedCostViability) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelFixedCostViabilityAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return <FunnelFixedCostViability markdown={funnelMarkdown} />;
            }
            if (isProductSkuEconomics) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelProductSkuEconomicsAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return <FunnelProductSkuEconomics markdown={funnelMarkdown} />;
            }
            if (isCustomerLifetimeEconomics) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelCustomerLifetimeEconomicsAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return (
                <FunnelCustomerLifetimeEconomics markdown={funnelMarkdown} />
              );
            }
            if (isGrowthEfficiencyChannelMix) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelGrowthEfficiencyAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return <FunnelGrowthEfficiency markdown={funnelMarkdown} />;
            }
            if (isMarginStabilityRisk) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelMarginStabilityAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return <FunnelMarginStability markdown={funnelMarkdown} />;
            }
            if (isOperationalReadinessDataConfidence) {
              if (hasFunnelAssessmentTitle) {
                return (
                  <FunnelOperationalReadinessAssessmentViewerV2
                    markdown={funnelMarkdown}
                  />
                );
              }
              return <FunnelOperationalReadiness markdown={funnelMarkdown} />;
            }

            if (
              isExecutiveSnapshot ||
              isPointA ||
              isPointB ||
              isTopInitiatives ||
              isRoadmap ||
              isRisksAndAssumptions ||
              isMarketReality ||
              isProductPerformanceReality ||
              isProductTruth ||
              isHomepageMessagingSignals ||
              isRevenueConcentrationRisk ||
              isCompetitivePressureSnapshot ||
              isCoreDiagnosis
            ) {
              return (
                <GenericFunnelViewer
                  markdown={funnelMarkdown}
                  title={funnelTitle}
                />
              );
            }
          }

          return null;
        };

        const hideHeaderForAssessment =
          assessmentKey === "indexing" ||
          assessmentKey === "sitePerformance" ||
          assessmentKey === "coreWebVitals" ||
          assessmentKey === "gscReport" ||
          assessmentKey === "merchantCenter";

        return (
          <>
            {hideHeaderForAssessment ? null : (
              <div className={styles.sectionHeader}>
                {metric ? (
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>{metric.label}</span>
                    <span className={styles.metricValue}>
                      {metric.value}
                      {metric.unit}
                    </span>
                    <div className={styles.metricBar}>
                      <div
                        className={styles.metricFill}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            <div className={styles.sectionBody}>
              {renderStageContent() ||
                (assessmentKey ? (
                  renderAssessment()
                ) : (
                  <>
                    {renderParagraphs(before)}
                    {tableBlock ? renderTable(tableBlock) : null}
                    {after ? renderParagraphs(after) : null}
                  </>
                ))}
            </div>
          </>
        );
      })()}
    </div>
  );

  if (!isOpen) return null;

  const contentNode = (
    <div
      className={`${styles.modal} ${variant === "page" ? styles.modalPage : styles.modalOverlayVariant}`}
      onClick={(event) => event.stopPropagation()}
      role="dialog"
      aria-modal={variant === "modal"}
    >
      <header className={styles.header}>
        {showCloseButton ? (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        ) : null}
      </header>

      <div
        className={`${styles.body} ${variant === "page" ? styles.bodyPage : ""}`}
      >
        {sections.length ? (
          <div className={styles.carousel}>
            <div
              className={`${styles.carouselTrack} ${isRTL ? styles.rtl : ""}`}
              ref={scrollRef}
              onScroll={handleScroll}
              style={{ visibility: isInitialPositionReady ? "visible" : "hidden" }}
            >
              {sections.map((section, idx) => (
                <div
                  key={`${section.title}-${idx}`}
                  className={styles.carouselSlide}
                  data-carousel-slide
                >
                  {renderSection(section, idx)}
                </div>
              ))}
            </div>
            {sections.length > 1 ? (
              <div className={styles.navRow}>
                <div className={styles.dotsWrapper}>
                  {sections.map((_, idx) => (
                    <button
                      key={`dot-${idx}`}
                      type="button"
                      className={`${styles.dot} ${idx === currentIndex ? styles.dotActive : ""}`}
                      onClick={() => scrollToIndex(idx)}
                      aria-label={`Go to section ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <p className={styles.empty}>No additional report content yet.</p>
        )}
      </div>
    </div>
  );

  if (variant === "page") {
    return <div className={styles.pageWrapper}>{contentNode}</div>;
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="presentation">
      {contentNode}
    </div>,
    document.body,
  );
};

export default StrategyFullReportModal;
